import { setLastSeenIndex } from '../actions/actions';
import { DefaultSettings } from "../../../../settings/settings-util";
import { Store } from "redux";
import { AlmeState, defaultState } from "../../../../wireup/AlmeState";
import { AlmeUIResponseArray, Message } from "../../../../types/response";
import LiveChatState from "../livechat-state";
import { AlmeResponseV5 } from "@alme/types/response/AlmeResponse";
import { OperatorData } from "../models/OperatorData";


export default class ResponseHandler {

    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _stateManager: LiveChatState;


    //#region Public Interface

    constructor(settings: DefaultSettings, stateManager: LiveChatState) {
        this._settings = settings;
        this._stateManager = stateManager;
    }

    initialize(store: Store<AlmeState>): void {
        this._store = store;
    }

    getReducer() {
        return (state = defaultState): AlmeState => state;
    }

    doesHandle(message: Message): boolean {
        const response = message?.response as AlmeResponseV5;

        return response?.extensionData?.MessageSource === this._settings.livechat.messageSource ||
            response?.extensionData?.MessageIndex !== undefined;
    }

    process(message: Message): Message {
        this.isNonHistoryArray(message) ?
            (message.response as AlmeUIResponseArray).forEach(response => this.processSingleMessage({ ...message, response })) :
            this.processSingleMessage(message);

        return message;
    }

    //#endregion



    //#region Process Methods

    processSingleMessage(message: Message): void {
        if (this.handlesStartAction(message) && this.containsStartAction(message)) {
            const { livechatQueuedBanner } = this._settings.messageConstants;

            this._stateManager.updateChatSessionState(this._stateManager.statuses.queued);

            //Feels dirty...queue should be rendered after response, so set timeout to let alme response be processed first
            setTimeout(() => this._stateManager.dispatchResponse(message.response as AlmeResponseV5, livechatQueuedBanner), 0);
        }

        if (this.containsEndAction(message)) {
            this._stateManager.dispatchResponse(message.response as AlmeResponseV5, this._settings.messageConstants.almeResponseType);
        }

        if (!this.doesHandle(message)) { return; }

        const response = message.response as AlmeResponseV5;
        const oldLiveChatState = this._stateManager.sessionStatus;
        const newLiveChatState = response.extensionData.SessionState as string;

        this.updateMessageIndex(response);
        this.checkForError(message);
        this._stateManager.updateChatSessionState(newLiveChatState);
        this.checkForQueueUpdates(response, newLiveChatState);
        this.checkForConnectionUpdate(response, newLiveChatState, oldLiveChatState);
        this.processOrigin(message);
    }

    updateMessageIndex(response: AlmeResponseV5): void {
        if (typeof response.extensionData.MessageIndex !== "undefined") {
            const { Base, LastSeenIndex } = this._settings.stateLocations.LiveChat;
            const stateLocation = `${Base}.${LastSeenIndex}`;

            this._store?.dispatch(setLastSeenIndex(stateLocation, response.extensionData.MessageIndex as number));
        }
    }

    checkForError(message: Message): void {
        if (message.error || (message.response as AlmeResponseV5)?.extensionData.IsError) {
            this._stateManager.handleErrorState();
        }
        else {
            this._stateManager.resetErrorCount();
        }
    }

    checkForQueueUpdates(response: AlmeResponseV5, newLiveChatState: string): void {
        const { livechatQueuedBanner } = this._settings.messageConstants;

        if (this._stateManager.isQueuedStatus(newLiveChatState) && !this._stateManager.historyContainsQueueBanners()) {
            this._stateManager.dispatchResponse(response, livechatQueuedBanner);
        }
        else if (!this._stateManager.isQueuedStatus(newLiveChatState) && this._stateManager.historyContainsQueueBanners()) {
            this._stateManager.removeQueueBanners();
        }
    }

    checkForConnectionUpdate(response: AlmeResponseV5, newLiveChatState: string, oldLiveChatState: string): void {
        const { livechatConnectBanner } = this._settings.messageConstants;

        if (this._stateManager.isJoining(newLiveChatState, oldLiveChatState)) {
            this._stateManager.dispatchResponse(response, livechatConnectBanner);
        }
    }

    processOrigin(message: Message): void {
        const { livechatDisconnectBanner, livechatInputType, livechatResponseType } = this._settings.messageConstants;
        const response = message.response as AlmeResponseV5;
        const origin = this._settings.livechat.messageOrigin;

        switch (response.extensionData.MessageOrigin) {
            case origin.operator:
                this._stateManager.dispatchResponse(response, livechatResponseType);
                break;

            case origin.operatorJoined:
                this._stateManager.saveOperatorData(response.extensionData.OperatorData as OperatorData);
                this._stateManager.dispatchResponse(response, livechatResponseType);
                break;

            case origin.operatorLeft:
                this._stateManager.dispatchResponse(response, livechatResponseType);
                break;

            case origin.operatorTyping:
                this._stateManager.updateOperatorTyping();
                break;

            case origin.sessionEnd:
                this._stateManager.dispatchResponse(response, livechatResponseType);
                this._stateManager.dispatchResponse(response, livechatDisconnectBanner);
                break;

            case origin.system:
                this._stateManager.saveQueueBannerText(response.text);
                break;

            case origin.user:
                this._stateManager.dispatchResponse(response, livechatInputType);
                break;
        }
    }

    //#endregion



    //#region Helper Methods

    handlesStartAction(message: Message): boolean {
        if (!this._settings.livechat.liveChatStartAction.length) { return false; }

        return [
            'ConverseRequest',
            'RequestUnitRequest',
            'AppeventRequest',
        ].includes(message.messageType ?? "");
    }

    containsStartAction(message: Message): boolean {
        const startAction = this._settings.livechat.liveChatStartAction;

        return !!(message.response as AlmeResponseV5)?.responseActions?.find(action => action.Name === startAction);
    }

    containsEndAction(message: Message): boolean {
        return !!(message.response as AlmeResponseV5)?.responseActions?.find(action => action.Name === "LiveChatEnd");
    }

    isNonHistoryArray(message: Message): boolean {
        return !!message.response &&
            Array.isArray(message.response) &&
            message.response.length > 0 &&
            message.messageType !== this._settings.messageConstants.getHistoryRequest;
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/livechat-foundation/src/handlers/response-handler.ts